import React, { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileText, X, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import ContactMethodPicker, { CONTACT_METHOD_LABEL } from "@/components/ContactMethodPicker";

const CASE_TYPES = [
  { value: "policy_review", label: "Pregled obstoječe police" },
  { value: "informative_calc", label: "Informativni izračun" },
  { value: "claim_help", label: "Pomoč pri zahtevkih" },
  { value: "injury_claim", label: "Odškodnina — telesna poškodba" },
  { value: "auto_claim", label: "Odškodnina — prometna nesreča" },
  { value: "other", label: "Drugo" },
];

export default function DocumentUpload() {
  const [files, setFiles] = useState([]); // [{id, original_filename, size, content_type, status, localFile}]
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    contact_method: "email",
    case_type: "policy_review",
    description: "",
  });
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const onPick = (e) => uploadFiles(Array.from(e.target.files || []));

  const onDrop = (e) => {
    e.preventDefault();
    dropRef.current?.classList.remove("ring-2");
    uploadFiles(Array.from(e.dataTransfer.files || []));
  };

  const uploadFiles = async (list) => {
    for (const f of list) {
      const tmpId = `tmp-${Math.random().toString(36).slice(2)}`;
      setFiles((prev) => [
        ...prev,
        { id: tmpId, original_filename: f.name, size: f.size, content_type: f.type, status: "uploading" },
      ]);
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) =>
          prev.map((x) => (x.id === tmpId ? { ...data, status: "done" } : x))
        );
      } catch (err) {
        toast.error(formatApiError(err));
        setFiles((prev) => prev.filter((x) => x.id !== tmpId));
      }
    }
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((x) => x.id !== id));

  const submit = async (e) => {
    e.preventDefault();
    if (form.contact_method === "email" && !form.email) {
      toast.error("Vnesite e-naslov.");
      return;
    }
    if ((form.contact_method === "phone" || form.contact_method === "whatsapp") && !form.phone) {
      toast.error("Vnesite telefonsko številko.");
      return;
    }
    setSubmitting(true);
    try {
      const ready = files.filter((x) => x.status === "done").map((x) => x.id);
      await api.post("/cases", {
        name: form.name || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        contact_method: form.contact_method,
        case_type: form.case_type,
        description: form.description || undefined,
        file_ids: ready,
      });
      setSubmitted(true);
      toast.success("Vaš primer je bil uspešno poslan.");
      setForm({
        name: "", email: "", phone: "", contact_method: "email",
        case_type: "policy_review", description: "",
      });
      setFiles([]);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="dokumenti"
      data-testid="documents-section"
      className="py-24 md:py-32"
      style={{ background: "var(--vp-bg)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-sm font-semibold tracking-widest uppercase text-[var(--vp-primary)] mb-3">
              Pomoč
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Naložite police ali dokumentacijo nesreče.
            </h2>
            <p className="mt-5 text-[var(--vp-muted)] leading-relaxed">
              Brezplačno pregledamo obstoječe police in poiščemo, kje plačujete preveč ali
              kje nimate kritja. Za odškodninske primere prosimo priložite fotografije
              poškodb, zdravniške izvide in zapisnik o nesreči.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                "Vse zavarovalnice in vse police na enem mestu",
                "Informativni izračun za zavarovanje v 24 urah",
                "Brez stroškov dokler ne uspemo",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 className="text-[var(--vp-primary)] mt-0.5" size={18} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="case-submitted"
                className="rounded-2xl border border-[var(--vp-border)] bg-white p-10 text-center"
              >
                <CheckCircle2 className="mx-auto text-[var(--vp-primary)]" size={48} />
                <h3 className="font-display text-2xl font-bold mt-4">Hvala za zaupanje!</h3>
                <p className="text-[var(--vp-muted)] mt-2">
                  Vašo dokumentacijo bomo pregledali v 24 urah in se vam oglasili na vaš
                  e-naslov ali telefon.
                </p>
                <Button
                  data-testid="case-submit-another"
                  onClick={() => setSubmitted(false)}
                  className="mt-6 rounded-full"
                  style={{ background: "var(--vp-primary)", color: "white" }}
                >
                  Pošlji nov primer
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-[var(--vp-border)] bg-white p-7 md:p-9 space-y-5">
                <div
                  ref={dropRef}
                  onDragOver={(e) => {
                    e.preventDefault();
                    dropRef.current?.classList.add("ring-2");
                  }}
                  onDragLeave={() => dropRef.current?.classList.remove("ring-2")}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  data-testid="dropzone"
                  className="cursor-pointer rounded-xl border-2 border-dashed border-[var(--vp-border)] hover:border-[var(--vp-primary)]/50 transition-colors p-8 text-center ring-[var(--vp-primary)]/40"
                >
                  <UploadCloud className="mx-auto text-[var(--vp-primary)]" size={36} />
                  <div className="mt-3 font-semibold">Povlecite datoteke sem ali kliknite za izbiro</div>
                  <div className="text-xs text-[var(--vp-muted)] mt-1">
                    PDF, JPG, PNG, WEBP · največ 15 MB na datoteko
                  </div>
                  <input
                    ref={inputRef}
                    data-testid="file-input"
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    className="hidden"
                    onChange={onPick}
                  />
                </div>

                {files.length > 0 && (
                  <div className="space-y-2" data-testid="file-list">
                    {files.map((f) => (
                      <div
                        key={f.id}
                        className="flex items-center justify-between rounded-lg border border-[var(--vp-border)] bg-[var(--vp-sand)]/50 px-3 py-2 text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={16} className="text-[var(--vp-primary)] flex-shrink-0" />
                          <span className="truncate">{f.original_filename}</span>
                          {f.status === "uploading" && (
                            <span className="text-xs text-[var(--vp-muted)]">nalaganje…</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(f.id)}
                          className="text-[var(--vp-muted)] hover:text-red-600 p-1"
                          aria-label="Odstrani"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <Label className="text-sm font-semibold">Kako vas naj kontaktiram?</Label>
                  <p className="text-xs text-[var(--vp-muted)] mt-0.5 mb-3">
                    Izbrano: {CONTACT_METHOD_LABEL[form.contact_method]}.
                  </p>
                  <ContactMethodPicker
                    value={form.contact_method}
                    onChange={(v) => setForm({ ...form, contact_method: v })}
                    testid="case-method"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Ime in priimek">
                    <Input
                      data-testid="case-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Neobvezno"
                    />
                  </Field>
                  <Field label={`Telefon${form.contact_method !== "email" ? " *" : ""}`}>
                    <Input
                      data-testid="case-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      required={form.contact_method !== "email"}
                      placeholder={form.contact_method !== "email" ? "Obvezno" : "Neobvezno"}
                    />
                  </Field>
                  <Field label={`E-naslov${form.contact_method === "email" ? " *" : ""}`}>
                    <Input
                      data-testid="case-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required={form.contact_method === "email"}
                      placeholder={form.contact_method === "email" ? "Obvezno" : "Neobvezno"}
                    />
                  </Field>
                  <Field label="Vrsta primera">
                    <Select
                      value={form.case_type}
                      onValueChange={(v) => setForm({ ...form, case_type: v })}
                    >
                      <SelectTrigger data-testid="case-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CASE_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label="Opis primera">
                  <Textarea
                    data-testid="case-description"
                    rows={4}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Na kratko opišite, kaj potrebujete (neobvezno)…"
                  />
                </Field>

                <Button
                  type="submit"
                  data-testid="case-submit"
                  disabled={submitting}
                  className="w-full rounded-full py-6 text-base font-semibold"
                  style={{ background: "var(--vp-primary)", color: "white" }}
                >
                  {submitting ? "Pošiljanje…" : "Pošlji v pregled"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
